import { EmptyObject } from 'libs/end-to-end/src/generic';
import { createCustomVizRequestSchema } from 'libs/end-to-end/src/custom-viz/createCustomVizRequestSchema';
import { z } from 'zod';
import { FilterDefinitionForExtras } from 'libs/end-to-end/src/types/databases/filters/filter-definition';
import { ViewByOption } from 'libs/end-to-end/src/view-by';
import { BigTimePeriodComparison } from 'libs/end-to-end/src/big-time-period-comparison';

export const TimeSeriesTrendsInputs = z.object({
  dropdowns: EmptyObject,
  extras: z.object({
    filters: z.array(FilterDefinitionForExtras),
    metric: z.string(),
    breakdownOption: z.string().nullable(),
    // This option name is actually the viewBy option if you are looking for it.
    timeBoiOption: ViewByOption,
    bigTimeComparisonOption: BigTimePeriodComparison,
    startDate: z.string(),
    endDate: z.string(),
    dateRangeType: z.string().nullish(),
    chartType: z.string().nullish(),
    treatmentDate: z.string().nullish(),
    treatmentGroup: z.string().nullish(),
  }),
});

export type TimeSeriesTrendsInputs = z.infer<typeof TimeSeriesTrendsInputs>;

export const TimeSeriesTrendsRequest = createCustomVizRequestSchema(
  'timeSeriesTrends',
  TimeSeriesTrendsInputs,
);

export type TimeSeriesTrendsRequest = z.infer<typeof TimeSeriesTrendsRequest>;

export const TimeSeriesTrendsResultBucketElement = z
  .object({
    group: z.string(),
    total: z.number().nullable(),
    numerator: z.number().nullable(),
    percentage: z.number().nullable(),
    isMetricBoolean: z.boolean().optional(),
  })
  .describe('TimeSeriesTrendsResultBucketElement');

export type TimeSeriesTrendsResultBucketElement = z.infer<
  typeof TimeSeriesTrendsResultBucketElement
>;

export const TimeSeriesTrendsResultBucket = z
  .object({
    start: z.string(),
    end: z.string(),
    elements: z.array(TimeSeriesTrendsResultBucketElement),
    fittedValue: z.number().nullish(),
    fittedCountValue: z.number().nullish(),
    controlValue: z.number().nullish(),
  })
  .describe('TimeSeriesTrendsResultBucket');

export type TimeSeriesTrendsResultBucket = z.infer<
  typeof TimeSeriesTrendsResultBucket
>;

export const TimeSeriesTrendsResult = z
  .object({
    buckets: z.array(TimeSeriesTrendsResultBucket),
    compareWithBuckets: z.array(TimeSeriesTrendsResultBucket).nullish(),
    treatmentDate: z.string().nullish(),
    beforeJump: z.number().nullish(),
    afterJump: z.number().nullish(),
    jump: z.number().nullish(),
    beforeJumpConfidence: z.number().nullish(),
    afterJumpConfidence: z.number().nullish(),
    jumpConfidence: z.number().nullish(),
    shadowBeforeTreatmentDate: z.boolean().nullish(),
  })
  .describe('TimeSeriesTrendsResult');

export type TimeSeriesTrendsResult = z.infer<typeof TimeSeriesTrendsResult>;
